import Grid from '@mui/material/Grid';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  changeCollectionsFilterGroupBy,
  findReliefsRequested,
  reportCollectionsFilterStartBusinessDateSelector,
  reportCollectionsFilterEndBusinessDateSelector,
  reportFilterCountryCodeSelector,
  reportCollectionsFilterGroupBySelector,
  reportReliefsLoadingSelector,
} from '../state';

import {
  CountryFilter,
  ReportActionsGridItem,
  ReportFilterActions,
  ReportFilters,
  ReportFiltersProps,
  StoreFilter,
} from './ReportFilters';

import { AppPermissions, ReportReliefsGroupBy } from '../types';
import { BusinessDateRangeFilter } from './CollectionsFilters';
import { PermissionsProvider } from './Permissions';
import { useOpStructureValidation } from '../hooks';
import { FilterPanel, StatusChip } from './Visual';

const groupByConfig: ReportFiltersProps<ReportReliefsGroupBy>['groupByConfig'] = {
  options: {
    countryAcronym: true,
    storeAcronym: false,
    businessDate: false,
  },
  action: changeCollectionsFilterGroupBy,
  selector: reportCollectionsFilterGroupBySelector,
};

const ReliefsSearch = () => {
  const { t } = useTranslation();
  const validateAndExecuteAction = useOpStructureValidation();
  const startDate = useSelector(reportCollectionsFilterStartBusinessDateSelector);
  const endDate = useSelector(reportCollectionsFilterEndBusinessDateSelector);
  const hasCountry = Boolean(useSelector(reportFilterCountryCodeSelector));
  const applyDisabled = !(startDate.accept && endDate.accept && hasCountry);
  const countryRequiredIncomplete = applyDisabled && !hasCountry;
  const businessDateRequiredIncomplete = applyDisabled && !(startDate.accept && endDate.accept);

  const validatedApplyAction = useCallback(
    () => validateAndExecuteAction(findReliefsRequested),
    [validateAndExecuteAction]
  );

  return (
    <PermissionsProvider resourcePermissions={[AppPermissions.RELIEFS]}>
      <ReportFilters
        groupByConfig={groupByConfig}
        loadingSelector={reportReliefsLoadingSelector}
        applyAction={validatedApplyAction}
        showOpStructureFilters
      >
        <FilterPanel
          subtitleTranslationKey="reliefs.filters.subtitle"
          actions={
            <StatusChip
              status={applyDisabled ? 'warning' : 'success'}
              label={
                applyDisabled ? t('filters.status.missingRequired') : t('filters.status.ready')
              }
            />
          }
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3} lg={2} xl={2}>
              <CountryFilter fullWidth requiredIncomplete={countryRequiredIncomplete} />
            </Grid>
            <Grid item xs={12} md={3} lg={2} xl={2}>
              <StoreFilter />
            </Grid>
            <Grid item xs={12} md={6} lg={4} xl={3}>
              <BusinessDateRangeFilter
                spacing={1}
                requiredIncomplete={businessDateRequiredIncomplete}
              />
            </Grid>
            <ReportActionsGridItem>
              <ReportFilterActions applyDisabled={applyDisabled} />
            </ReportActionsGridItem>
          </Grid>
        </FilterPanel>
      </ReportFilters>
    </PermissionsProvider>
  );
};

export default ReliefsSearch;
